<script setup lang='ts'>
import { APP_ROUTER } from '@qctsw/common'
import User from './components/User.vue'
import Car from './components/Car.vue'
import Complaint from './components/Complaint.vue'
import ModalPublishComplete from './components/ModalPublishComplete.vue'
import RelativeComplaint from './components/RelativeComplaint.vue'
import { formatPublishParams, usePublishSubmit } from './hooks/publish'
import { useAgain } from './hooks/again'
import { useAuto } from './hooks/auto'
import { validateHandle } from '~/shared/function'
import { LOAD_STATE } from '~/shared/types'

const { IS_LOGIN, IS_SUBMIT } = storeToRefs(useAuth())
const publishStore = usePublishStore()
const { formData } = storeToRefs(publishStore)
const currentStep = ref(0) // 当前step
const step = {
  childCount: 4,
  componentsList: [
    { title: '车主信息' },
    { title: '车辆信息' },
    { title: '投诉内容' },
    { title: '投诉完成' },
  ],
}
function changeCurrentStep(data: number) {
  window.scrollTo(0, 0)
  currentStep.value = data
}
onActivated(() => {
  currentStep.value = 0
})

const route = useRoute()
const { againInitHandle } = useAgain()
const { getDefaultDriving } = useAuto()
const carRef = ref<typeof Car | null>(null)

const state = ref(LOAD_STATE.INIT)
async function initHandler() {
  if (!IS_LOGIN.value)
    return useAuth().needLoginHandle()

  const complaintId = route.query.complaintId as string | undefined
  const collectiveId = route.query.collectiveId as string | undefined
  const brandId = route.query.brandId as string | undefined
  const seriesId = route.query.seriesId as string | undefined

  const isSite = route.query.isSite || false as string | undefined | boolean
  formData.value.isSite = !!(isSite)

  state.value = LOAD_STATE.LOADING
  // 再次投诉
  if (complaintId && !Number.isNaN(Number(complaintId)))
    await againInitHandle(Number(complaintId))
  else if (formData.value.refComplainId)
    publishStore.reset() // 如果不是再次投诉，但是缓存的数据是时，清除缓存

  if (IS_LOGIN.value && !formData.value.carSeriesId) {
  // 如果无已填写内容，获取默认车辆信息填写
    formData.value.refComplainContent = undefined
    formData.value.refComplainId = undefined
    await getDefaultDriving()
  }
  // 参加集体投诉
  if (collectiveId && !Number.isNaN(Number(collectiveId))) {
    formData.value.collectiveId = Number(collectiveId)
    carRef.value?.getCollectiveHotList({ collectiveId })
  }

  // 投诉指定品牌车系
  if (brandId && !Number.isNaN(Number(brandId))) {
    formData.value.carBrandId = Number(brandId)
    formData.value.carSeriesId = seriesId ? Number(seriesId) : undefined
  }

  state.value = LOAD_STATE.END
}

const visibleCompleteDialog = ref(false)
const relationParams = ref({
  brandId: 4,
  seriesId: 8199,
  provinceId: 440000,
  cityId: 440100,
})

const userRef = ref<typeof User | null>(null)

const complaintRef = ref<typeof Complaint | null>(null)
async function reValidate() {
  const forms = [
    userRef.value?.formRef,
    carRef.value?.formRef,
    complaintRef.value?.formRef,
  ]
  for (const index in forms) {
    const { isOk } = await validateHandle(forms[index])
    if (!isOk) {
      changeCurrentStep(Number(index))
      useMessage.error('请完善信息')
      return false
    }
  }
  return true
}

const { publishSubmit, createDriving } = usePublishSubmit()

async function submit() {
  // 重新校验下数据
  if (!(await reValidate()))
    return
  // 格式化数据
  const params = formatPublishParams()

  if (!params)
    return
  // 提交数据
  const result = await publishSubmit(params)
  if (!result)
    return
  const { formData, reset } = usePublishStore()
  if (!formData.isGetDefaultDriving)
    createDriving(params)

  IS_SUBMIT.value = true
  window.removeEventListener('beforeunload', windowBeforeunload)
  relationParams.value = {
    brandId: params.brandId as number,
    seriesId: params.seriesId as number,
    cityId: params.carDealerCityId,
    provinceId: params.carDealerProvinceId,
  }

  visibleCompleteDialog.value = true
  changeCurrentStep(3)

  reset()
}

// definePageMeta({
//   middleware: ['publish-abort'],
// })

useActivatedWatch(() => [
  watch(IS_LOGIN, () => {
    initHandler()
  }, { immediate: true }),
])

function windowBeforeunload(e) {
  if (process.client && IS_LOGIN.value) {
    const confirmationMessage = '您的投诉暂未提交，确定要离开吗?';
    (e || window.event).returnValue = confirmationMessage // 兼容 Gecko + IE
    return confirmationMessage // 兼容 Gecko + Webkit, Safari, Chrome
  }
}

onMounted(() => {
  IS_SUBMIT.value = false
  window.addEventListener('beforeunload', windowBeforeunload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', windowBeforeunload)
})

onDeactivated(() => {
  window.removeEventListener('beforeunload', windowBeforeunload)
})

onActivated(() => {
  IS_SUBMIT.value = false
  window.addEventListener('beforeunload', windowBeforeunload)
})
</script>

<template>
  <div bg-slate-1>
    <div class="public-container public-page">
      <div class="bg-white rounded-md p-2xl px-30">
        <BasicStep type="custom" :custom="step" :active="currentStep" top="-3px" width="40px">
          <template #default="{ item, index }">
            <div class="w-10 h-10 leading-10 text-5.5 rounded-5 text-white">
              {{ index + 1 }}
            </div>
            <div class="mt-4 text-3.5 whitespace-nowrap">
              {{ item.title }}
            </div>
          </template>
        </BasicStep>
      </div>

      <template v-if="!IS_LOGIN">
        <div class="w-full flex-center py-20">
          <NButton type="info" quaternary round @click="useLoginPopupEvent().showLoginPopup('phone')">
            您还未登录,请点击此处进行登录
          </NButton>
        </div>
      </template>
      <template v-if="state === LOAD_STATE.END">
        <div v-if="currentStep !== 3">
          <User
            v-show="currentStep === 0"
            ref="userRef"
            @change-current-step="changeCurrentStep"
          />

          <ClientOnly>
            <Car
              v-show="currentStep === 1"
              ref="carRef"
              @change-current-step="changeCurrentStep"
            />
            <Complaint
              v-show="currentStep === 2"
              ref="complaintRef"
              @change-current-step="changeCurrentStep"
              @submit="submit"
            />
          </ClientOnly>
          <div class="mb-4 flex items-center justify-center mt-sm">
            <NCheckbox v-if="currentStep !== 3" v-model:checked="formData.isAccept">
              我已阅读并且同意
              <NuxtLink class="text-blue-5" target="_blank" :to="APP_ROUTER.helpDetail(formData.isSite ? 30 : 14)">
                《隐私政策》
              </NuxtLink>、
              <NuxtLink class="text-blue-5" target="_blank" :to="APP_ROUTER.helpDetail(formData.isSite ? 29 : 20)">
                《上传服务》
              </NuxtLink>
            </NCheckbox>
          </div>
        </div>

        <RelativeComplaint
          v-else
          class="bg-white rounded-md px-2xl py-sm mt-xl"
          :brand-id="Number(relationParams.brandId)"
          :series-id="Number(relationParams.seriesId)"
          :city-id="Number(relationParams.cityId)"
          :province-id="Number(relationParams.provinceId)"
        />
        <ModalPublishComplete v-model="visibleCompleteDialog" />
      </template>
    </div>
  </div>
</template>

<style lang='scss' scoped>
:deep(div.step-line) {
  height: 6px;
}
.step-item {
  --uno: text-slate-2;

  &>div:first-child {
  --uno: bg-slate-3;
  }

  &.is-active {
    &>div:first-child {
    --uno: bg-blue-5;
    }
    --uno: bg-blue-5;
  }
}
</style>
